/**
 * How note names are spelled and displayed.
 *
 * Purely presentational. MIDI numbers and frequencies never pass through here
 * — a transposing instrument changes what a player calls a pitch, not the
 * pitch itself.
 */

import { SEMITONES_PER_OCTAVE } from './pitch'

/** Supported naming systems. */
export type Notation = 'english' | 'german' | 'solfeggio'

/** Names for pitch classes 0-11 in each system. */
const NAMES: Record<Notation, readonly string[]> = {
  english: ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'],
  /**
   * German keeps sharps as "-is" and renames the seventh degree: English B is
   * German H, and English Bb is German B. Because this table is written with
   * sharps, pitch class 10 appears as Ais rather than B.
   */
  german: ['C', 'Cis', 'D', 'Dis', 'E', 'F', 'Fis', 'G', 'Gis', 'A', 'Ais', 'H'],
  solfeggio: ['Do', 'Do#', 'Re', 'Re#', 'Mi', 'Fa', 'Fa#', 'Sol', 'Sol#', 'La', 'La#', 'Si'],
}

/** Labels for the notation picker. */
export const NOTATION_LABELS: Record<Notation, string> = {
  english: 'Englisch (C D E …)',
  german: 'Deutsch (C D E … H)',
  solfeggio: 'Solfège (Do Re Mi …)',
}

/** Every notation, in menu order. */
export const NOTATIONS: readonly Notation[] = ['english', 'german', 'solfeggio']

/** Narrow an arbitrary string to a known notation. */
export function toNotation(value: string | null): Notation {
  return value === 'german' || value === 'solfeggio' || value === 'english' ? value : 'english'
}

/** Lowest and highest supported transposition, in semitones. */
export const MIN_TRANSPOSITION = -5
export const MAX_TRANSPOSITION = 6

/** Default: concert pitch instrument, nothing shifted. */
export const DEFAULT_TRANSPOSITION = 0

/** Every transposition from G (-5) to F# (+6). */
export const TRANSPOSITIONS: readonly number[] = Array.from(
  { length: MAX_TRANSPOSITION - MIN_TRANSPOSITION + 1 },
  (_, index) => MIN_TRANSPOSITION + index,
)

/** Clamp a value into the supported transposition range. */
export function toTransposition(value: number | null): number {
  if (value === null || !Number.isFinite(value)) return DEFAULT_TRANSPOSITION
  const rounded = Math.round(value)
  if (rounded < MIN_TRANSPOSITION) return MIN_TRANSPOSITION
  if (rounded > MAX_TRANSPOSITION) return MAX_TRANSPOSITION
  return rounded
}

/** Pitch class 0-11, correct for negative inputs. */
function pitchClass(value: number): number {
  return ((value % SEMITONES_PER_OCTAVE) + SEMITONES_PER_OCTAVE) % SEMITONES_PER_OCTAVE
}

/**
 * Name the key an instrument is in, for the transposition picker.
 *
 * The label is the note that written C sounds as, so a shift of -5 reads G
 * and a shift of +2 reads D.
 */
export function transpositionLabel(semitones: number, notation: Notation): string {
  const names = NAMES[notation]
  return names[pitchClass(semitones)] ?? '?'
}

/**
 * Name a pitch for display.
 *
 * @param midi - Sounding MIDI note.
 * @param notation - Naming system.
 * @param transposition - Semitones to shift the *name* by; 0 is concert pitch.
 * @returns Note name without octave.
 */
export function noteName(midi: number, notation: Notation, transposition = 0): string {
  const names = NAMES[notation]
  return names[pitchClass(Math.round(midi) + transposition)] ?? '?'
}

/**
 * Octave number for a displayed pitch, following the same shift as the name.
 *
 * MIDI 0 is C-1, so the octave is offset by one from a plain division.
 */
export function noteOctave(midi: number, transposition = 0): number {
  return Math.floor((Math.round(midi) + transposition) / SEMITONES_PER_OCTAVE) - 1
}

/** Name plus octave, e.g. "E2" or "H3". */
export function noteLabel(midi: number, notation: Notation, transposition = 0): string {
  return `${noteName(midi, notation, transposition)}${String(noteOctave(midi, transposition))}`
}
